import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb, { type Crumb } from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { topics } from "@/mocks/news";
import { articlesByTopic, getTopic } from "@/utils/news";

export default function TopicPage() {
  const { topicId = "" } = useParams();
  const topic = getTopic(topicId);

  if (!topic) {
    return (
      <div className="max-w-3xl mx-auto w-full px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-4xl text-foreground-400">
          <i className="ri-error-warning-line" />
        </div>
        <h1 className="mt-5 text-xl font-heading font-black text-foreground-950">
          该专题暂不存在
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          专题可能已下线，去看看其他精选内容吧。
        </p>
        <Link
          to="/"
          className="inline-block mt-6 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const list = articlesByTopic(topic.id);
  const others = topics.filter((t) => t.id !== topic.id);
  const crumbs: Crumb[] = [{ label: "专题" }, { label: topic.title }];

  return (
    <div>
      <Seo
        title={`${topic.title} - 专题 - 51爆料`}
        description={topic.intro.slice(0, 80)}
        keywords={`${topic.title},51爆料专题,热点专题`}
      />

      <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src={topic.cover}
          alt={topic.title}
          title={`${topic.title} - 51爆料专题`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" />
        <div className="absolute inset-0 w-full px-4 md:px-6 flex items-end pb-8 md:pb-12">
          <div className="max-w-7xl mx-auto w-full text-white">
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-accent-500 text-foreground-950 mb-3">
              <i className="ri-links-line" />
              51爆料专题
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-black">
              {topic.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/85 max-w-3xl">
              {topic.intro}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-6">
        <Breadcrumb items={crumbs} />
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 pb-16">
        <div className="bg-accent-50 border border-accent-200 rounded-lg p-5 md:p-6 mb-8">
          <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950 mb-2">
            <i className="ri-quill-pen-line text-accent-600" />
            策划介绍
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed">
            {topic.reason}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
          <div>
            <div className="flex items-end justify-between mb-5">
              <h2 className="text-lg md:text-xl font-heading font-black text-foreground-950">
                专题收录（{list.length} 篇）
              </h2>
            </div>

            {list.length > 0 ? (
              <div className="flex flex-col gap-4">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} layout="row" />
                ))}
              </div>
            ) : (
              <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 px-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                  <i className="ri-file-list-line" />
                </div>
                <p className="mt-4 text-base font-medium text-foreground-800">
                  该专题内容正在整理中
                </p>
                <p className="mt-1 text-sm text-foreground-500">
                  编辑正在补充更多爆料，敬请期待。
                </p>
                <Link
                  to="/"
                  className="inline-block mt-5 px-4 py-2 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  先去首页看看
                </Link>
              </div>
            )}
          </div>

          <aside>
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950 mb-4">
                <i className="ri-apps-line text-primary-500" />
                相关专题
              </h2>
              <ul className="space-y-4">
                {others.map((t) => (
                  <li key={t.id}>
                    <Link to={`/topic/${t.id}`} className="block group cursor-pointer">
                      <div className="w-full h-24 rounded-md overflow-hidden mb-2">
                        <img
                          src={t.cover}
                          alt={t.title}
                          title={`${t.title} - 51爆料专题`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {t.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}